import { useMemo } from 'react'
import type { AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { TimelineElement } from '../../../../types/timelineElement'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UseAssigneeLoadModuleParams {
  workspaceId: string
  snapshot: AnalysisSnapshot
  elements?: TimelineElement[]
  timeRange: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export interface UseAssigneeLoadModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate()
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

export const useAssigneeLoadModule = ({
  workspaceId,
  snapshot,
  elements = [],
  timeRange,
  refreshKey,
  now = new Date(),
}: UseAssigneeLoadModuleParams): UseAssigneeLoadModuleResult => {
  return useMemo(() => {
    void refreshKey

    const assigneeLoad = snapshot.team.assigneeLoad
    if (!workspaceId || assigneeLoad.length === 0) {
      return {
        headline: { value: '—', label: 'no assignees' },
        sparkline: { points: [], direction: 'up-is-good' },
        records: [],
        loading: false,
        error: null,
      }
    }

    const { from, to } = timeRangeToDateRange(timeRange, now)
    const assignedDates: Date[] = []
    for (const element of elements) {
      if (element.workspaceId !== workspaceId) continue
      for (const item of element.checklist ?? []) {
        const timestampFields = item as typeof item & {
          assignedTimestamp?: unknown
          updatedAt?: unknown
          createdAt?: unknown
        }
        const assignedAt = parseDate(
          timestampFields.assignedAt
          ?? timestampFields.assignedTimestamp
          ?? timestampFields.updatedAt
          ?? timestampFields.createdAt,
        )
        if (assignedAt && assignedAt >= from && assignedAt <= to) assignedDates.push(assignedAt)
      }
    }

    const points = assignedDates.length > 0 ? bucketize(assignedDates, timeRange, now) : []
    const records: LiveModuleRecord[] = assigneeLoad
      .slice(0, 5)
      .map((row) => ({
        id: row.id,
        primary: row.label,
        secondary: `${row.completed} completed · ${row.overdue} overdue`,
        numeric: row.total,
        drillTarget: 'CHECKLISTS',
      }))

    return {
      headline: {
        value: assigneeLoad.length,
        label: 'assignees',
        delta: points.length > 0 ? calculateDelta(points) : 0,
      },
      sparkline: { points, direction: 'up-is-good' },
      records,
      loading: false,
      error: null,
    }
  }, [workspaceId, snapshot, elements, timeRange, refreshKey, now])
}
